import type { User } from "@shared/schema";

// Should the client route this user to the payment step (before the wizard / app)? Mirrors the
// server-side gate's intent but is ONLY for routing/UX — the authoritative enforcement is the backend
// requireActiveAccess gate (it 402s regardless of what the client does). Admins bypass; trial/active and
// legacy-null (grandfathered) users pass; only pending_payment / expired are routed to pay.
export function needsPayment(user: User | undefined, paymentRequired: boolean): boolean {
  if (!paymentRequired || !user) return false;
  if (user.role === "admin") return false;
  return user.accessState === "pending_payment" || user.accessState === "expired";
}

export type EntryScreen = "landing" | "payment" | "checkin" | "app";

// One routing decision for every signed-in route (plan Rev. 3 item 27): pay first, then the check-in
// (step 1 "Ihr Hotel" creates the company), then the app. The waitlist redirect for `status='pending'`
// stays with the caller because it is a navigation, not a screen. `paymentForced` comes from the client 402
// guard (lib/billingGate.ts): the server keeps answering 402 although /api/auth/me still reports access.
export function resolveEntryScreen(
  user: User | undefined,
  paymentRequired: boolean,
  paymentForced = false,
): EntryScreen {
  if (!user) return "landing";
  if (needsPayment(user, paymentRequired)) return "payment";
  if (paymentForced && user.role !== "admin") return "payment";
  if (!user.isOnboarded) return "checkin";
  return "app";
}

// Start page inside the app (plan Rev. 3 item 27): the check-in stays the start page until the hotel hides
// it (companies.checklist_dismissed_at) or chose "Zum Dashboard" in this browser session.
export function resolveStartPage(
  company: { checklistDismissedAt?: string | Date | null } | null | undefined,
  skippedThisSession: boolean,
): "checkin" | "cockpit" {
  if (!company || company.checklistDismissedAt || skippedThisSession) return "cockpit";
  return "checkin";
}

// Stripe statuses of a failed renewal (server/services/stripeAccess.PAYMENT_ISSUE_STATUSES).
const FAILED_RENEWAL_STATUSES = ["past_due", "unpaid"];

// Lapsed grace after a failed renewal: Stripe still holds the subscription, so checkout would answer 409
// ALREADY_SUBSCRIBED and only the billing portal (new card) brings access back. /api/auth/me carries
// subscriptionStatus but not stripeCustomerId, so this is the signal the PaymentStep can rely on.
export function isFailedRenewal(
  user: { accessState?: string | null; subscriptionStatus?: string | null } | null | undefined,
): boolean {
  return user?.accessState === "expired" && FAILED_RENEWAL_STATUSES.includes(user.subscriptionStatus ?? "");
}
